import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { H4, Small } from "@/components/ui/typography"
import React from "react"
import { Checkbox } from "../ui/checkbox"
import { Form, FormDescription, FormField, FormItem, FormLabel } from "../ui/form"
import { useForm, UseFormReturn, useWatch } from "react-hook-form"
import { MileageFormData } from "@/state/draft/mileage"
import { TimeSpent } from "@/state/draft/timeSpent"
import { RHFProvider } from "./RHFProvider"
import { mi, mi2, usd, usd2, usd3 } from "@/utils/formaters"
import { getMileageRate } from "@/utils/mileageRate"
import { RATES } from "@/constants/rates"
import { PlainDate } from "@/utils/date"

type MileageFormProps =
	| {
			advancedFormEnabled: true
			form: UseFormReturn<MileageFormData>
			timeSpentForm: UseFormReturn<TimeSpent>
			dateRange: PlainDate[]
	  }
	| {
			advancedFormEnabled: false
			form: UseFormReturn<MileageFormData>
			timeSpentForm?: UseFormReturn<TimeSpent>
			dateRange: PlainDate[]
	  }

export const MileageForm: React.FC<MileageFormProps> = ({
	advancedFormEnabled,
	form,
	timeSpentForm,
	dateRange,
}) => {
	const [showAdvanced, setShowAdvanced] = React.useState(false)

	React.useEffect(() => {
		const subscription = form.watch((value, { name }) => {
			if (name === "startOdometer" || name === "endOdometer") {
				const start = Number(value.startOdometer) || 0
				const end = Number(value.endOdometer) || 0
				if (end > start) {
					form.setValue("totalMiles", String(end - start))
				} else {
					form.setValue("totalMiles", "0")
				}
			}
		})

		return subscription.unsubscribe
	}, [form])

	useWatch({ control: form.control })

	const totalMiles = +form.getValues("totalMiles")
	const startRate = getMileageRate(dateRange[0], RATES)
	const endRate = getMileageRate(dateRange[1], RATES)
	let minRate = Math.min(startRate, endRate)
	let maxRate = Math.max(startRate, endRate)

	let avgRate = (minRate + maxRate) / 2

	let totalPredictedDeduction = avgRate * totalMiles
	const potentialDeductionText = `~${usd(totalPredictedDeduction)} potential deduction`

	return (
		<FormField
			control={form.control}
			name="inputMethod"
			render={({ field }) => (
				<FormItem>
					<RadioGroup
						onValueChange={field.onChange}
						value={field.value}
						className="gap-0"
					>
						<div className="space-y-2">
							<H4>
								How many business miles would you like to claim in your
								report?
							</H4>
							<div className="flex items-center space-x-2 mt-3">
								<RadioGroupItem value="odometer" id="odometer" />
								<Label htmlFor="odometer">
									Odometer Readings (From — To)
								</Label>
							</div>
							<div className="flex items-center space-x-2 pl-6">
								<FormField
									control={form.control}
									name="startOdometer"
									rules={{
										required: "Start odometer reading is required",
										min: 0,
									}}
									render={({ field }) => (
										<FormItem>
											<Input
												{...field}
												type="number"
												placeholder="00000 mi"
												onFocus={() => {
													form.setValue(
														"inputMethod",
														"odometer"
													)
												}}
											/>
										</FormItem>
									)}
								/>
								<span>—</span>
								<FormField
									control={form.control}
									name="endOdometer"
									rules={{
										required: "End odometer reading is required",
										min: 0,
										validate: (value) => {
											if (form.getValues().inputMethod === "total")
												return true

											if (+value > +form.getValues("startOdometer"))
												return true

											return "End odometer must be greater than start odometer"
										},
									}}
									render={({ field }) => (
										<FormItem>
											<Input
												{...field}
												type="number"
												placeholder="00000 mi"
												onFocus={() => {
													form.setValue(
														"inputMethod",
														"odometer"
													)
												}}
											/>
										</FormItem>
									)}
								/>
							</div>
							<Small className="pl-6 text-muted-foreground font-normal">
								{totalMiles > 0
									? `Your mileage for the report: ${mi(
											totalMiles
									  )} (${potentialDeductionText})`
									: "Enter your starting and ending odometer readings."}
							</Small>
						</div>
						<div className="space-y-2 mt-4">
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="total" id="total" />
								<Label htmlFor="total">Total Business Miles</Label>
							</div>
							<div className="pl-6">
								<FormField
									control={form.control}
									name="totalMiles"
									render={({ field }) => (
										<FormItem>
											<Input
												{...field}
												type="number"
												placeholder="00000 mi"
												className="w-40"
												onFocus={() => {
													form.setValue("inputMethod", "total")
												}}
											/>
										</FormItem>
									)}
								/>
							</div>
							<Small className="pl-6 text-muted-foreground font-normal">
								{totalMiles > 0
									? potentialDeductionText
									: "These miles will be included in your report"}
							</Small>
						</div>
						{advancedFormEnabled && (
							<div className="mt-8">
								<div className="flex items-center space-x-2">
									<Checkbox
										id="advanced"
										checked={showAdvanced}
										onCheckedChange={(checked) =>
											setShowAdvanced(checked as boolean)
										}
										className="border-[#344cff]"
									/>
									<Label htmlFor="advanced" className="font-medium">
										Advanced Settings
									</Label>
								</div>
								{showAdvanced && (
									<RHFProvider form={timeSpentForm}>
										<AdvancedForm form={timeSpentForm} />
									</RHFProvider>
								)}
							</div>
						)}
					</RadioGroup>
				</FormItem>
			)}
		/>
	)
}

type AdvancedFormProps = {
	form: UseFormReturn<TimeSpent>
}
const AdvancedForm: React.FC<AdvancedFormProps> = ({ form }) => {
	return (
		<div className="mt-4 space-y-6 rounded-lg border border-[#e2e8f0] p-6">
			<div className="space-y-4">
				<FormField
					control={form.control}
					name="tripsPerDay"
					render={() => {
						return (
							<FormItem>
								<FormLabel>
									Number of Business Trips per Day{" "}
									<span className="font-normal">(From — To)</span>
								</FormLabel>
								<div className="flex items-center gap-4 mt-2">
									<FormField
										control={form.control}
										name="tripsPerDay.from"
										render={({ field }) => {
											return (
												<Input
													type="number"
													{...field}
													placeholder="0"
													className="border-[#e2e8f0]"
												/>
											)
										}}
									/>
									<span className="text-[#64748b]">—</span>
									<FormField
										control={form.control}
										name="tripsPerDay.to"
										render={({ field }) => {
											return (
												<Input
													{...field}
													type="number"
													placeholder="0"
													className="border-[#e2e8f0]"
												/>
											)
										}}
									/>
								</div>
								<FormDescription>
									How many business trips do you typically make in a
									workday?
								</FormDescription>
							</FormItem>
						)
					}}
				/>

				<FormField
					control={form.control}
					render={({}) => {
						return (
							<FormItem>
								<FormItem>
									<FormLabel className="font-medium">
										Time Spent at Business Locations
										<span className="font-normal">(From — To)</span>
									</FormLabel>
									<div className="flex items-center gap-4 mt-2">
										<FormField
											control={form.control}
											name="timeAtLocation.from"
											render={({ field }) => {
												return (
													<Input
														{...field}
														type="number"
														placeholder="0 min"
														className="border-[#e2e8f0]"
													/>
												)
											}}
										/>
										<span className="text-[#64748b]">—</span>
										<FormField
											control={form.control}
											name="timeAtLocation.to"
											render={({ field }) => {
												return (
													<Input
														{...field}
														type="number"
														placeholder="0 min"
														className="border-[#e2e8f0]"
													/>
												)
											}}
										/>
									</div>
									<FormDescription>
										Specify the time you spend with clients, in
										meetings, deliveries, with suppliers, etc.
									</FormDescription>
								</FormItem>
							</FormItem>
						)
					}}
					name={"timeAtLocation"}
				/>

				<FormField
					control={form.control}
					render={({}) => {
						return (
							<FormItem>
								<FormItem>
									<FormLabel className="font-medium">
										Time Spent in Office
										<span className="font-normal">(From — To)</span>
									</FormLabel>
									<div className="flex items-center gap-4 mt-2">
										<FormField
											control={form.control}
											name="timeInOffice.from"
											render={({ field }) => {
												return (
													<Input
														{...field}
														type="number"
														placeholder="0 min"
														className="border-[#e2e8f0]"
													/>
												)
											}}
										/>
										<span className="text-[#64748b]">—</span>
										<FormField
											control={form.control}
											name="timeInOffice.to"
											render={({ field }) => {
												return (
													<Input
														{...field}
														type="number"
														placeholder="0 min"
														className="border-[#e2e8f0]"
													/>
												)
											}}
										/>
									</div>
									<FormDescription>
										Indicate how much time you spend in the office on
										a typical workday
									</FormDescription>
								</FormItem>
							</FormItem>
						)
					}}
					name={"timeInOffice"}
				/>
			</div>
		</div>
	)
}
